import React, { useRef, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FaHome, 
  FaBoxOpen, 
  FaTools, 
  FaUsers, 
  FaPhone, 
  FaBars, 
  FaTimes,
  FaChevronDown,
  FaChevronRight,
  FaTh,
  FaGlobe,
  FaSearch
} from 'react-icons/fa';

const THEME = {
  primary: '#0e7490',
  primaryMain: '#0891b2',
  primaryLight: '#06b6d4',
  secondary: '#155e75',
  accent: '#4b7cbb',
  text: '#1e293b',
  textSecondary: '#334155',
  bgWhite: '#ffffff',
  bgLight: '#f8fafc',
  gradient: 'linear-gradient(135deg, #0e7490 0%, #0891b2 100%)',
};

const REGIONS = [
  {
    id: 'main',
    name: 'ภาษาที่รองรับ',
    languages: [
      { code: 'th', label: 'ไทย (Thai)', flag: 'TH' },
      { code: 'en', label: 'English', flag: 'EN' },
      { code: 'zh-CN', label: '中文 (Chinese)', flag: 'CN' }
    ]
  }
];

function useOnClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) return;
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}

const Navbar = ({
  menuOpen,
  toggleMenu,
  closeMenu,
  scrolled,
  hidden,
  categories = [],
}) => {
  const location = useLocation();
  const navRef = useRef();
  const dropdownRef = useRef();
  const hoverTimer = useRef();
  const [dropdownHover, setDropdownHover] = useState(false);
  
  // ── Mobile: toggle สำหรับ dropdown สินค้า ──
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langDropdownRef = useRef();
  useOnClickOutside(langDropdownRef, () => setLangMenuOpen(false));

  const getInitialLang = () => {
    const match = document.cookie.match(/(?:^|; )googtrans=([^;]*)/);
    if (match && match[1]) {
      const code = match[1].split('/')[2] || match[1].split('/')[1];
      return code === 'en' ? 'en' : code;
    }
    return localStorage.getItem('appLanguage') || 'th';
  };

  const [currentLang, setCurrentLang] = useState(getInitialLang());

  const handleGoogleTranslate = (langCode) => {
    try {
      setCurrentLang(langCode);
      localStorage.setItem('appLanguage', langCode);
      setLangMenuOpen(false); 
      const selectEl = document.querySelector('.goog-te-combo');
      if (selectEl) {
        selectEl.value = langCode;
        selectEl.dispatchEvent(new Event('change', { bubbles: true }));
      } 
      if (langCode === 'th') {
        document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${window.location.hostname}; path=/;`;
      } else {
        document.cookie = `googtrans=/th/${langCode}; path=/;`;
        document.cookie = `googtrans=/th/${langCode}; domain=${window.location.hostname}; path=/;`;
      }
      window.location.reload();
    } catch (err) {
      console.error("Error changing language", err);
    }
  };

  const TEXTS = {
    home: 'หน้าแรก',
    products: 'สินค้า',
    services: 'บริการ',
    about: 'เกี่ยวกับเรา',
    contact: 'ติดต่อเรา',
    allProducts: 'ดูสินค้าทั้งหมด',
    categories: 'หมวดหมู่สินค้า',
    searchPlaceholder: 'ค้นหาหมวดหมู่...',
  };

  useOnClickOutside(navRef, () => { if (menuOpen) closeMenu(); });
  useOnClickOutside(dropdownRef, () => { setDropdownHover(false); setSearchQuery(''); });

  const isActive = (path) => location.pathname === path;

  const openDropdown = () => { clearTimeout(hoverTimer.current); setDropdownHover(true); };
  const closeDropdown = () => {
    hoverTimer.current = setTimeout(() => { setDropdownHover(false); setSearchQuery(''); }, 130);
  };

  // ── ปิด mobile dropdown เมื่อปิด menu ──
  useEffect(() => {
    if (!menuOpen) setMobileDropdownOpen(false);
  }, [menuOpen]);

  const categoryGroups = categories.reduce((acc, cat) => {
    const main = cat.main_category || 'หมวดหมู่อื่นๆ';
    if (!acc[main]) {
      acc[main] = {
        items: [],
        priority: (cat.main_priority !== undefined && cat.main_priority !== null) ? cat.main_priority : 999
      };
    }
    acc[main].items.push(cat);
    if (cat.main_priority < acc[main].priority) {
      acc[main].priority = cat.main_priority;
    }
    return acc;
  }, {});

  const mainCategoryNames = Object.keys(categoryGroups).sort((a, b) => {
    return categoryGroups[a].priority - categoryGroups[b].priority;
  });

  const groupedCategories = {};
  for (const key in categoryGroups) {
    groupedCategories[key] = categoryGroups[key].items;
  }
  
  const [activeMainCategory, setActiveMainCategory] = useState('');

  useEffect(() => {
    if (mainCategoryNames.length > 0 && !activeMainCategory) {
      setActiveMainCategory(mainCategoryNames[0]);
    }
  }, [categories, mainCategoryNames, activeMainCategory]);

  const filteredSubCategories = (groupedCategories[activeMainCategory] || []).filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (location.pathname === '/admin') return null;

  const currentLangData = REGIONS.flatMap(r => r.languages).find(l => l.code === currentLang) || REGIONS[0].languages[0];

  return (
    <>
      <style>{`
/* ════════════════════════════════
   BASE — Gradient Fade Navbar
════════════════════════════════ */
.navbar {
  position: fixed; top: 0; left: 0; width: 100%;
  height: 110px; 
  background: linear-gradient(to bottom, 
    rgba(255, 255, 255, 0.98) 0%, 
    rgba(255, 255, 255, 0.6) 50%, 
    rgba(255, 255, 255, 0) 100%);
  pointer-events: none; 
  z-index: 1000;
  transition: all 0.4s ease;
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 1.2rem 2.5rem;
  border: none;
}
.brand-section, .nav-links, .hamburger { pointer-events: auto; }
.navbar.scrolled {
  height: 80px; 
  padding: 0.8rem 2.5rem;
  background: rgba(255, 255, 255, 0.95); 
  backdrop-filter: blur(15px); 
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  align-items: center;
  pointer-events: auto; 
}
.navbar.hidden { transform: translateY(-100%); }

/* ════════════════════════════════
    BRAND SECTION
════════════════════════════════ */
.brand-section { z-index:1001; flex-shrink:0; }
.brand-link { display:flex; align-items:center; text-decoration:none; gap:12px; }
.logo-img {
  height: 50px; object-fit: contain;
  filter: drop-shadow(0 2px 8px rgba(0,0,0,0.1));
  transition: transform .3s ease;
}
.brand-link:hover .logo-img { transform: scale(1.05); }
.brand-text {
  display: flex; flex-direction: column; justify-content: center;
  border-left: 2px solid rgba(0, 0, 0, 0.15);
  padding-left: 12px; gap: 2px;
}
.brand-name, .brand-slogan {
  margin: 0; font-size: 0.95rem; font-weight: 700;
  color: #111111; line-height: 1.3; letter-spacing: 1.5px;
}

/* ════════════════════════════════
    NAV LINKS
════════════════════════════════ */
.nav-links { display:flex; align-items:center; gap:.25rem; margin-left:auto; }
.nav-btn {
  display:flex; align-items:center; gap:.5rem;
  padding:.6rem 1.1rem; color: #1e293b;
  text-decoration:none; font-size:1rem; font-weight:600;
  border-radius:8px; transition:all 0.25s ease;
  position:relative; white-space:nowrap;
  border:none; background:transparent; cursor:pointer;
}
.nav-btn:hover { color: #0e7490; background: rgba(14, 116, 144, 0.07); transform: translateY(-1px); }
.nav-btn.active { color: #0e7490; font-weight: 700; }
.nav-btn.active::after {
  content:''; position:absolute; bottom: 2px; left:50%;
  transform:translateX(-50%); width:55%; height:2px;
  background: #0e7490; border-radius:10px;
}
.contact-btn {
  background: linear-gradient(135deg, #0e7490 0%, #0891b2 100%) !important;
  color: #ffffff !important; font-weight: 600;
  padding: .65rem 1.5rem !important; border-radius: 50px;
  box-shadow: 0 4px 15px rgba(6, 182, 212, 0.35); margin-left: .75rem;
}
.contact-btn:hover {
  background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%) !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(6, 182, 212, 0.45); color: #ffffff !important;
}
.current-lang-btn {
  display: flex; align-items: center;
  background: rgba(14, 116, 144, 0.07);
  border: 1px solid rgba(14, 116, 144, 0.20);
  padding: 6px 14px; border-radius: 20px;
  cursor: pointer; color: #0e7490;
  font-weight: 600; font-size: 0.9rem; transition: all 0.2s ease;
}
.current-lang-btn:hover { background: rgba(14, 116, 144, 0.13); }
.hamburger {
  display:none; cursor:pointer; color: #0e7490;
  font-size:1.8rem; padding:.5rem; border-radius:10px;
  transition:all .3s ease; z-index:1001;
}
.hamburger:hover { background: rgba(14, 116, 144, 0.08); transform:scale(1.1); }

/* ═══════════════════════════════════
   PRODUCTS MEGA DROPDOWN — Desktop
═══════════════════════════════════ */
.nav-item-container { position:relative; display:flex; align-items:center; }
.mega-drop {
  position: absolute; top: calc(100% + 10px); left: 50%;
  transform: translateX(-50%) translateY(-6px);
  width: 780px; background: #fff; border-radius: 16px;
  border: 1px solid rgba(14,116,144,0.10);
  box-shadow: 0 15px 35px rgba(0,0,0,0.10);
  opacity: 0; visibility: hidden; pointer-events: none; z-index: 200;
  overflow: hidden; transition: opacity .20s ease, transform .20s ease, visibility .20s;
  display: flex; flex-direction: column;
}
.mega-drop::before {
  content: ''; position: absolute; top: -5px; left: 50%; transform: translateX(-50%) rotate(45deg);
  width: 12px; height: 12px; background: #fff;
  border-top: 1px solid rgba(14,116,144,0.10); border-left: 1px solid rgba(14,116,144,0.10);
  border-radius: 2px 0 0 0; z-index: 1;
}
.mega-drop.is-open { opacity: 1; visibility: visible; pointer-events: auto; transform: translateX(-50%) translateY(0); }

.md-head { 
  display: flex; align-items: center; justify-content: space-between; 
  padding: 12px 20px;
  background: linear-gradient(135deg, rgba(14,116,144,0.05) 0%, rgba(6,182,212,0.02) 100%); 
  border-bottom: 1px solid rgba(14,116,144,0.09); 
}
.md-head-left { display: flex; align-items: center; gap: 10px; }
.md-icon-box { width: 32px; height: 32px; border-radius: 8px; background: linear-gradient(135deg,#0e7490,#0891b2); display: flex; align-items: center; justify-content: center; color: #fff; font-size: 14px; }
.md-head-title { font-size: 1.1rem; font-weight: 700; color: #0e7490; }
.md-all-btn { display: flex; align-items: center; gap: 5px; text-decoration: none; font-size: 0.90rem; font-weight: 600; color: #0891b2; padding: 6px 12px; border-radius: 8px; border: 1px solid rgba(8,145,178,0.2); transition: all .2s ease; }
.md-all-btn:hover { background: rgba(8,145,178,0.08); color: #0e7490; }

.md-search-row { position: relative; padding: 10px 20px; border-bottom: 1px solid #f1f5f9; }
.md-search-ico { position: absolute; left: 32px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.md-search { width: 100%; height: 36px; border: 1px solid #e2e8f0; border-radius: 8px; padding: 0 15px 0 35px; font-size: 0.90rem; background: #f8fafc; outline: none; transition: all .2s; }
.md-search:focus { border-color: #0891b2; background: #fff; box-shadow: 0 0 0 3px rgba(8,145,178,0.1); }

.mega-menu-split { display: flex; height: 400px; align-items: stretch; }
.mega-sidebar { width: 35%; background: #f8fafc; border-right: 1px solid #e2e8f0; padding: 10px 0; overflow-y: auto; }
.mega-content { width: 65%; padding: 20px 25px; background: #ffffff; overflow-y: auto; }
.mega-sidebar::-webkit-scrollbar, .mega-content::-webkit-scrollbar { width: 6px; }
.mega-sidebar::-webkit-scrollbar-thumb, .mega-content::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
.mega-sidebar::-webkit-scrollbar-track, .mega-content::-webkit-scrollbar-track { background: transparent; }

.md-item-new {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; border-radius: 8px; 
  text-decoration: none; transition: all 0.2s ease;
  background: #ffffff; border: 1px solid #bae6fd;
  box-shadow: 0 1px 2px rgba(0,0,0,0.02);
}
.md-item-new:hover {
  background: #f0f9ff; border-color: #7dd3fc;
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(2, 132, 199, 0.1);
}

/* ═══════════════════════════════════
   LANGUAGE MEGA MENU
═══════════════════════════════════ */
.lang-switcher-container { position: relative; display: flex; align-items: center; margin-left: 1rem; }
.lang-mega-wrapper {
  position: absolute; top: calc(100% + 15px); right: 0; width: 220px;
  background: white; border-radius: 12px;
  box-shadow: 0 15px 35px rgba(0,0,0,0.15);
  border: 1px solid rgba(14,116,144,0.10); opacity: 0; visibility: hidden;
  transform: translateY(-10px); transition: all 0.25s ease;
  z-index: 1000; overflow: hidden;
}
.lang-mega-wrapper.show { opacity: 1; visibility: visible; transform: translateY(0); }
.lang-mega-header { padding: 12px 15px; background: ${THEME.gradient}; color: white; display: flex; align-items: center; gap: 12px; font-size: 1rem; font-weight: 700; border-radius: 12px 12px 0 0; }
.lang-grid { display: flex; flex-direction: column; padding: 10px; gap: 4px; }
.lang-option-btn {
  width: 100%; text-align: left; padding: 10px 15px;
  background: transparent; border: none; cursor: pointer;
  color: ${THEME.text}; font-size: 0.90rem; border-radius: 8px;
  transition: all 0.2s; display: flex; align-items: center; gap: 10px;
  font-family: inherit;
}
.lang-option-btn:hover { background: rgba(14,116,144,0.06); color: ${THEME.primary}; }
.lang-option-btn.active { background: rgba(14,116,144,0.1); color: ${THEME.primaryMain}; font-weight: 700; }
.lang-flag { font-size: 0.90rem; font-weight: 700; color: ${THEME.primary}; width: 28px; display: inline-block; flex-shrink: 0; }

/* ════════════════════════════════════════
   MOBILE (≤768px)
════════════════════════════════════════ */
@media (max-width: 768px) {
  .navbar {
    padding: 1rem 1.5rem; height: 80px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(15px);
    pointer-events: auto;
  }
  .logo-img { height: 40px; }
  .brand-name, .brand-slogan { font-size: 0.8rem; letter-spacing: 0.5px; }

  .nav-links {
    position: fixed; top: 0; left: -100%;
    width: 320px; max-width: 85vw; height: 100vh;
    background: #ffffff;
    flex-direction: column; align-items: stretch;
    padding: 5rem 0 2rem; gap: 0; margin: 0;
    box-shadow: 4px 0 30px rgba(0,0,0,0.1);
    transition: left .4s ease;
    overflow-y: auto; z-index: 1000;
  }
  .nav-links.open { left: 0; }
  .nav-links::before {
    content: ''; position: fixed; inset: 0;
    background: rgba(0,0,0,.5); backdrop-filter: blur(2px);
    opacity: 0; visibility: hidden; transition: opacity .3s ease;
    z-index: -1; pointer-events: none;
  }
  .nav-links.open::before { opacity: 1; visibility: visible; pointer-events: auto; }

  .nav-btn {
    margin: .25rem 1rem; padding: 1rem 1.5rem;
    border-left: 4px solid transparent;
    border-radius: 8px; font-size: 1rem;
    justify-content: flex-start; color: #1e293b;
  }
  .nav-btn:hover { background: #f1f5f9; border-left-color: #0891b2; color: #0f172a; transform: none; }
  .nav-btn.active { background: #f0f9ff; border-left-color: #0891b2; color: #0891b2; }
  .nav-btn.active::after { display: none; }
  .contact-btn { margin: 1.5rem 1rem 0 !important; justify-content: center !important; }
  .hamburger { display: flex; align-items: center; justify-content: center; }

  /* ── Mobile: Products dropdown toggle row ── */
  .nav-item-container { flex-direction: column; align-items: stretch; }

  /* ปุ่มแถวสินค้า (ซ่อน Link ไปหน้า /products ชั่วคราว ให้กดเพื่อเปิด dropdown แทน) */
  .mobile-products-row {
    display: flex; align-items: center; justify-content: space-between;
    margin: .25rem 1rem; padding: 1rem 1.5rem;
    border-left: 4px solid transparent;
    border-radius: 8px; cursor: pointer;
    color: #1e293b; font-size: 1rem; font-weight: 600;
    transition: all 0.2s;
  }
  .mobile-products-row:hover,
  .mobile-products-row.open { background: #f0f9ff; border-left-color: #0891b2; color: #0891b2; }

  .mobile-products-row-left { display: flex; align-items: center; gap: .5rem; }

  .mobile-dropdown-arrow {
    font-size: 0.75rem; transition: transform 0.25s ease; color: #94a3b8;
  }
  .mobile-dropdown-arrow.open { transform: rotate(180deg); color: #0891b2; }

  /* Desktop mega-drop — ซ่อนทั้งหมดในมือถือ */
  .mega-drop {
    position: static !important;
    width: auto !important;
    transform: none !important;
    box-shadow: none !important;
    border: none !important;
    border-radius: 0 !important;
    opacity: 1 !important;
    visibility: visible !important;
    pointer-events: auto !important;
    display: none;
    margin: 0;
    background: transparent !important;
  }
  .mega-drop::before { display: none; }
  .mega-drop.is-open { display: block; }

  /* ── Split layout ปรับให้เหมาะมือถือ ── */
  .mega-menu-split {
    flex-direction: column !important;
    height: auto !important;
    max-height: 70vh;
    overflow: hidden;
  }

  /* Sidebar แนวนอน (scroll ซ้าย-ขวา) */
  .mega-sidebar {
    width: 100% !important;
    border-right: none !important;
    border-bottom: 2px solid #e2e8f0 !important;
    padding: 0 !important;
    display: flex !important;
    flex-direction: row !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    white-space: nowrap;
    background: #f8fafc !important;
    flex-shrink: 0;
  }
  .mega-sidebar::-webkit-scrollbar { height: 3px; }
  .mega-sidebar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }

  /* main-cat-item ในมือถือ */
  .main-cat-item {
    border-left: none !important;
    border-bottom: 3px solid transparent !important;
    padding: 12px 16px !important;
    flex-shrink: 0;
    font-size: 0.88rem !important;
  }
  .main-cat-item.active {
    border-bottom-color: #0ea5e9 !important;
    background: #fff !important;
  }

  /* Content area (scroll แนวตั้ง) */
  .mega-content {
    width: 100% !important;
    max-height: calc(70vh - 50px) !important;
    padding: 12px 16px !important;
    overflow-y: auto !important;
  }

  /* sub-category grid แนวตั้ง 1 คอลัมน์ */
  .md-grid { grid-template-columns: 1fr !important; }

  /* Header และ Search ใน mobile */
  .md-head { padding: 10px 16px; }
  .md-head-title { font-size: 1rem; }
  .md-search-row { padding: 8px 16px; }
  .mega-drop .md-head,
  .mega-drop .md-search-row { background: #f8fafc; }

  /* ── Language switcher mobile ── */
  .lang-switcher-container { margin: 1.5rem 1rem 0; justify-content: flex-start; }
  .lang-mega-wrapper {
    position: absolute; top: calc(100% + 10px); right: 0;
    width: 220px; z-index: 1100;
  }
}

.nav-links::-webkit-scrollbar { width: 6px; }
.nav-links::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
      `}</style>

      <header
        className={`navbar ${scrolled ? 'scrolled' : ''}`}
        ref={navRef}
      >
        {/* Brand */}
        <div className="brand-section">
          <Link to="/" onClick={closeMenu} className="brand-link">
            <img src="/assets/logo121.png" alt="Ascent Corporation" className="logo-img" />
            <div className="brand-text">
              <span className="brand-name">ASCENT</span>
              <span className="brand-slogan">CORPORATION CO., LTD</span>
            </div>
          </Link>
        </div>

        {/* Hamburger */}
        <div className="hamburger" onClick={toggleMenu}>
          {menuOpen ? <FaTimes /> : <FaBars />}
        </div>

        {/* Nav */}
        <nav className={`nav-links ${menuOpen ? 'open' : ''}`}>

          <Link to="/" onClick={closeMenu} className={`nav-btn ${isActive('/') ? 'active' : ''}`}>
            <FaHome /> {TEXTS.home}
          </Link>

          {/* Products — Desktop: hover dropdown / Mobile: tap toggle */}
          <div
            className="nav-item-container"
            ref={dropdownRef}
            onMouseEnter={openDropdown}
            onMouseLeave={closeDropdown}
          >
            {/* ── แถวสินค้าในมือถือ (แสดงเฉพาะ mobile ผ่าน CSS) ── */}
            <div
              className={`mobile-products-row ${mobileDropdownOpen ? 'open' : ''}`}
              onClick={() => setMobileDropdownOpen(prev => !prev)}
            >
              <span className="mobile-products-row-left">
                <FaBoxOpen /> {TEXTS.products}
              </span>
              <FaChevronDown
                className={`mobile-dropdown-arrow ${mobileDropdownOpen ? 'open' : ''}`}
              />
            </div>

            {/* ── Link ปกติสำหรับ Desktop ── */}
            <Link
              to="/products"
              onClick={closeMenu}
              className={`nav-btn ${isActive('/products') ? 'active' : ''}`}
              style={{ display: 'none' }} /* CSS override ด้านล่าง */
            >
              <FaBoxOpen /> {TEXTS.products}
              <FaChevronDown
                size={12}
                style={{
                  opacity: .70,
                  transform: dropdownHover ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform .20s ease',
                }}
              />
            </Link>

            <div className={`mega-drop ${(dropdownHover || mobileDropdownOpen) ? 'is-open' : ''}`}>
              <div className="md-head">
                <div className="md-head-left">
                  <div className="md-icon-box"><FaTh /></div>
                  <span className="md-head-title">{TEXTS.categories}</span>
                </div>
                <Link
                  to="/products"
                  state={{ selectedCategory: 'ทั้งหมด' }}
                  onClick={() => { closeMenu(); setMobileDropdownOpen(false); }}
                  className="md-all-btn"
                >
                  {TEXTS.allProducts} <FaChevronRight size={10} />
                </Link>
              </div>

              {categories.length > 0 && (
                <div className="md-search-row">
                  <FaSearch className="md-search-ico" />
                  <input
                    className="md-search"
                    type="text"
                    placeholder={TEXTS.searchPlaceholder}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onClick={e => e.stopPropagation()}
                  />
                </div>
              )}

              <div className="mega-menu-split">
                {/* Sidebar: หมวดหมู่หลัก */}
                <div className="mega-sidebar">
                  {mainCategoryNames.map((mainCat, idx) => (
                    <div
                      key={idx}
                      className={`main-cat-item ${activeMainCategory === mainCat ? 'active' : ''}`}
                      onMouseEnter={() => setActiveMainCategory(mainCat)}
                      onClick={() => setActiveMainCategory(mainCat)}
                      style={{
                        padding: '12px 20px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        color: activeMainCategory === mainCat ? '#0284c7' : '#475569',
                        fontWeight: activeMainCategory === mainCat ? '700' : '500',
                        background: activeMainCategory === mainCat ? '#ffffff' : 'transparent',
                        borderLeft: `3px solid ${activeMainCategory === mainCat ? '#0ea5e9' : 'transparent'}`,
                        transition: 'background 0.2s ease',
                        fontSize: '0.95rem'
                      }}
                    >
                      {mainCat}
                      <FaChevronRight size={10} style={{ opacity: activeMainCategory === mainCat ? 1 : 0.2 }} />
                    </div>
                  ))}
                  {mainCategoryNames.length === 0 && (
                    <div style={{ padding: '20px', color: '#94a3b8', textAlign: 'center', fontSize: '0.9rem' }}>กำลังโหลด...</div>
                  )}
                </div>

                {/* Content: หมวดหมู่ย่อย */}
                <div className="mega-content">
                  <h4 style={{ color: '#0f172a', marginBottom: '15px', paddingBottom: '10px', borderBottom: '2px solid #f1f5f9', fontSize: '1.1rem', fontWeight: '700' }}>
                    {activeMainCategory || 'หมวดหมู่'}
                  </h4>
                  <div className="md-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px', paddingBottom: '20px' }}>
                    {filteredSubCategories.map((cat, idx) => (
                      <Link
                        key={idx}
                        to="/products"
                        state={{ selectedCategory: cat.name }}
                        onClick={() => { closeMenu(); setMobileDropdownOpen(false); }}
                        className="md-item-new"
                      >
                        <span style={{ color: '#0ea5e9', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1' }}>•</span>
                        <span style={{ fontSize: '0.95rem', color: '#0369a1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {cat.name}
                        </span>
                      </Link>
                    ))}
                    {filteredSubCategories.length === 0 && (
                      <div style={{ gridColumn: '1 / -1', color: '#94a3b8', padding: '20px', textAlign: 'center', background: '#f8fafc', borderRadius: '8px' }}>
                        ไม่พบหมวดหมู่ย่อย
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Link to="/services" onClick={closeMenu} className={`nav-btn ${isActive('/services') ? 'active' : ''}`}>
            <FaTools /> {TEXTS.services}
          </Link>

          <Link to="/about" onClick={closeMenu} className={`nav-btn ${isActive('/about') ? 'active' : ''}`}>
            <FaUsers /> {TEXTS.about}
          </Link>

          <Link to="/contact" onClick={closeMenu} className="nav-btn contact-btn">
            <FaPhone /> {TEXTS.contact}
          </Link>

          {/* Language Switcher */}
          <div className="lang-switcher-container" ref={langDropdownRef}>
            <button
              className="current-lang-btn"
              onClick={() => setLangMenuOpen(!langMenuOpen)}
            >
              <FaGlobe style={{ fontSize: '1rem', marginRight: '6px', opacity: 0.8 }} />
              <span style={{ marginRight: '6px' }}>{currentLangData.flag}</span>
              <FaChevronDown
                style={{
                  fontSize: '0.7rem',
                  transition: 'transform 0.2s ease',
                  transform: langMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              />
            </button>

            <div className={`lang-mega-wrapper ${langMenuOpen ? 'show' : ''}`}>
              <div className="lang-mega-header">
                <FaGlobe size={16} />
                <span>Select Language</span>
              </div>
              <div className="lang-grid">
                {REGIONS[0].languages.map((lang) => (
                  <button
                    key={lang.code}
                    className={`lang-option-btn ${currentLang === lang.code ? 'active' : ''}`}
                    onClick={() => handleGoogleTranslate(lang.code)}
                  >
                    <span className="lang-flag">{lang.flag}</span>
                    <span className="lang-label">{lang.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

        </nav>
      </header>

      {/* ── CSS override: ซ่อน/แสดง desktop link vs mobile row ── */}
      <style>{`
        /* Desktop: แสดง nav-btn link, ซ่อน mobile-products-row */
        @media (min-width: 769px) {
          .mobile-products-row { display: none !important; }
          .nav-item-container > a.nav-btn { display: flex !important; }
        }
        /* Mobile: แสดง mobile-products-row, ซ่อน desktop nav-btn link */
        @media (max-width: 768px) {
          .nav-item-container > a.nav-btn { display: none !important; }
          .mobile-products-row { display: flex !important; }
        }
      `}</style>
    </>
  );
};

export default Navbar;